import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Grid,
  Chip,
  Tooltip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Card
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Villa as VillaIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
  FileUpload as FileUploadIcon,
} from '@mui/icons-material';
import ExportButton from '../../../components/ExportButton/ExportButton';
import RefreshButton from '../../../components/RefreshButton/RefreshButton';
import {
  exportAsCSV,
  exportAsExcel,
  exportAsPDF,
  readExcelFile,
} from '../../../utils/importExport';
import { getProjects, getPlotsByProject } from '../../../services/project';
import { hasReadPermission, hasWritePermission } from '../../../utils/menuFilter';
import WarningIcon from '@mui/icons-material/Warning';
import AvatarMui from '@mui/material/Avatar';

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const Villa = () => {
  const location = useLocation();
  const history = useHistory();
  const theme = useTheme();
  const projectName = location.state?.projectName;

  const canRead = hasReadPermission('project.villa') || hasReadPermission('project');
  const canWrite = hasWritePermission('project.villa') || hasWritePermission('project');

  // State management
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [loadingVillas, setLoadingVillas] = useState(false);
  const [villaTypeOptions, setVillaTypeOptions] = useState([]);
  const villaTypesLoadedRef = useRef(false);
  const [facingOptions, setFacingOptions] = useState([]);
  const facingLoadedRef = useRef(false);
  const [progressOptions, setProgressOptions] = useState([]);
  const progressLoadedRef = useRef(false);
  const [deleteLoadingId, setDeleteLoadingId] = useState(null);
  
  const [projectFilter, setProjectFilter] = useState(projectName || 'all');
  const [pendingProjectFilter, setPendingProjectFilter] = useState(projectName || 'all');
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedVilla, setSelectedVilla] = useState(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importError, setImportError] = useState('');
  const [importSuccess, setImportSuccess] = useState('');
  const [importFileName, setImportFileName] = useState('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editFormData, setEditFormData] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [amenityOptions, setAmenityOptions] = useState([]);
  const fileInputRef = useRef(null);
  const [villas, setVillas] = useState([]);

  const pushSuccessMessage = (message) => {
    setErrorMessage('');
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const pushErrorMessage = (message) => {
    setSuccessMessage('');
    setErrorMessage(message);
    setTimeout(() => setErrorMessage(''), 4000);
  };

  const normalizeInventoryList = (res) => {
    if (Array.isArray(res)) return res;
    if (Array.isArray(res?.data)) return res.data;
    if (Array.isArray(res?.inventories)) return res.inventories;
    return [];
  };

  useEffect(() => {
    const fetchProjects = async () => {
      setLoadingProjects(true);
      try {
        const projectsData = await getProjects({ projectType: 'Villa' });
        setProjects(projectsData || []);
      } catch (error) {
        console.error('Failed to fetch villa projects:', error);
        setProjects([]);
      } finally {
        setLoadingProjects(false);
      }
    };
    fetchProjects();
  }, []);

  const loadVillaTypes = useCallback(async () => {
    if (villaTypesLoadedRef.current) return;
    villaTypesLoadedRef.current = true;
    try {
      const response = await fetch(`${API_BASE}/api/globalMasterDetails?type=villaType`);
      const body = await response.json();
      const list = body?.data || [];
      setVillaTypeOptions(list.map(item => ({ id: item._id, label: item.details?.villaType || item.name || '' })));
    } catch (error) {
      console.error('Failed to load villa types', error);
    }
  }, []);

  const loadProgressOptions = useCallback(async () => {
    if (progressLoadedRef.current) return;
    progressLoadedRef.current = true;
    setProgressOptions([
      { id: 'Available', label: 'Available' },
      { id: 'Booked', label: 'Booked' },
      { id: 'underConstruction', label: 'Under Construction' },
      { id: 'Hold', label: 'Hold' },
    ]);
  }, []);

  const loadFacingOptions = useCallback(async () => {
    if (facingLoadedRef.current) return;
    facingLoadedRef.current = true;
    try {
      const response = await fetch(`${API_BASE}/api/globalMasterDetails?type=facing`);
      const body = await response.json();
      const list = body?.data || [];
      setFacingOptions(list.map(item => ({ id: item._id, label: item.details?.facingName || item.name || '' })));
    } catch (error) {
      console.error('Failed to load facing options', error);
    }
  }, []);

  useEffect(() => {
    loadVillaTypes();
    loadProgressOptions();
    loadFacingOptions();
    fetchAmenities();
  }, [loadVillaTypes, loadProgressOptions, loadFacingOptions]);

  const fetchAmenities = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/globalMasterDetails?type=amenities`);
      const body = await response.json();
      const list = body?.data || [];
      setAmenityOptions(list.map(item => item.details?.amenitiesName || item.name || '').filter(Boolean));
    } catch (error) {
      console.error('Failed to load amenities', error);
    }
  };

  const getVillaTypeLabel = useCallback((value) => {
    const match = villaTypeOptions.find(opt => opt.id === value || opt.label === value);
    return match?.label || value;
  }, [villaTypeOptions]);

  const getProgressLabel = useCallback((value) => {
    const match = progressOptions.find(opt => opt.id === value || opt.label === value);
    return match?.label || value;
  }, [progressOptions]);

  const getFacingLabel = useCallback((value) => {
    const match = facingOptions.find(opt => opt.id === value || opt.label === value);
    return match?.label || value;
  }, [facingOptions]);

  const fetchVillaInventory = useCallback(async () => {
    const filterValue = String(pendingProjectFilter || 'all');
    if (filterValue === 'all') {
      setVillas([]);
      setTotalCount(0);
      return;
    }

    const selectedProject = projects.find(p => (p.projectName || p.name).toLowerCase() === filterValue.toLowerCase());
    if (!selectedProject) return;

    setLoadingVillas(true);
    try {
      const projectId = selectedProject._id || selectedProject.id;
      const res = await getPlotsByProject(projectId, 'Villa', 'Villa', null, page + 1, rowsPerPage);
      
      setTotalCount(res?.pagination?.totalItems || res?.count || 0);
      const inventories = normalizeInventoryList(res);
      const activeInventories = inventories.filter(item => !item.isDeleted);

      const mappedVillas = activeInventories.map((item, index) => {
        const rawVillaType = item.villaType || item.type || '';
        const statusValue = item.status || 'Available';
        const facingValue = item.facing || '';

        return {
          id: item._id || item.id,
          villaId: item.plotName || `VIL-${index + 1}`,
          name: item.plotName || `Villa ${index + 1}`,
          project: selectedProject.projectName || selectedProject.name,
          badge: getVillaTypeLabel(rawVillaType) || 'Premium',
          badgeColor: '#1976d2',
          status: getProgressLabel(statusValue) || 'Available',
          type: getVillaTypeLabel(rawVillaType),
          plotArea: item.totalSize ? `${item.totalSize} sq.ft` : '—',
          plotWidth: item.width ? `${item.width} ft` : '—',
          plotDepth: item.length ? `${item.length} ft` : '—',
          builtUpArea: item.BuiltUpArea || item.builtUpArea || '—',
          floors: item.villaFloor || item.floors || '—',
          facing: getFacingLabel(facingValue) || '—',
          carpetArea: item.carpetArea || '—',
          rate: item.pricePerSqFt ? `₹${item.pricePerSqFt.toLocaleString('en-IN')} / sq.ft` : '—',
          rateType: item.rateType || '—',
          companyDiscountPercent: item.companyDiscountPercent || 0,
          discountPerSqFt: item.discountPerSqFt || 0,
          discountAmount: item.discountAmount || 0,
          discount: `${item.companyDiscountPercent || 0}%`,
          plcPercent: item.plcPercent ? `${item.plcPercent}%` : '—',
          amount: item.amount ? `₹${item.amount.toLocaleString('en-IN')}` : '—',
          guidelineValue: item.guidelineValue ? `₹${item.guidelineValue.toLocaleString('en-IN')}` : '—',
          remark: item.remark || '—',
          amenities: Array.isArray(item.amenities) ? item.amenities : [],
          _rawData: item,
        };
      });

      setVillas(mappedVillas);
      setProjectFilter(pendingProjectFilter);
    } catch (error) {
      console.error('Failed to fetch villas:', error);
      pushErrorMessage('Failed to load villas.');
    } finally {
      setLoadingVillas(false);
    }
  }, [pendingProjectFilter, projects, page, rowsPerPage, getVillaTypeLabel, getProgressLabel, getFacingLabel]);

  useEffect(() => {
    if (projects.length > 0) {
      fetchVillaInventory();
    }
  }, [fetchVillaInventory, projects.length]);

  const handleAddNew = () => {
    const selectedProject = projects.find(p => (p.projectName || p.name).toLowerCase() === String(pendingProjectFilter).toLowerCase());
    const projectId = selectedProject?._id || selectedProject?.id || '';
    history.push(`/app/project/villa/add${projectId ? `?projectId=${projectId}` : ''}`);
  };

  const handleEdit = (villa) => {
    const raw = villa._rawData || {};
    setEditFormData({
      id: villa.id,
      projectId: raw.projectId || '',
      projectName: villa.project || '',
      plotName: raw.plotName || villa.name || '',
      type: raw.villaType || villa.type || '',
      facing: raw.facing || villa.facing || '',
      width: raw.width || (villa.plotWidth ? villa.plotWidth.split(' ')[0] : ''),
      length: raw.length || (villa.plotDepth ? villa.plotDepth.split(' ')[0] : ''),
      totalSize: raw.totalSize || (villa.plotSize ? villa.plotSize.split(' ')[0] : ''),
      builtUpArea: raw.BuiltUpArea || (villa.builtUpArea ? villa.builtUpArea.split(' ')[0] : ''),
      carpetArea: raw.carpetArea || (villa.carpetArea ? villa.carpetArea.split(' ')[0] : ''),
      floors: raw.totalFloors || villa.floors || '',
      rateType: raw.rateType || villa.rateType || 'Built-up Area',
      pricePerSqFt: raw.pricePerSqFt || (villa.rate ? villa.rate.replace(/[^0-9.]/g, '') : ''),
      companyDiscountPercent: raw.companyDiscountPercent || 0,
      discountPerSqFt: raw.discountPerSqFt || 0,
      discountAmount: raw.discountAmount || 0,
      plcPercent: raw.plcPercent || 0,
      amount: raw.amount || (villa.amount ? villa.amount.replace(/[^0-9.]/g, '') : ''),
      guidelineValue: raw.guidelineValue || (villa.guidelineValue ? villa.guidelineValue.replace(/[^0-9.]/g, '') : ''),
      status: raw.status || 'Available',
      remark: raw.remark || villa.remark || '',
      amenities: Array.isArray(raw.amenities) ? raw.amenities : [],
    });
    setEditDialogOpen(true);
  };

  const handleEditFieldChange = (field, value) => {
    setEditFormData(prev => {
      const updated = { ...prev, [field]: value };
      if (field === 'width' || field === 'length') {
        const w = Number(field === 'width' ? value : prev.width);
        const l = Number(field === 'length' ? value : prev.length);
        if (w && l) updated.totalSize = (w * l).toFixed(2);
      }

      // Add calculation logic for amount and discounts
      const rateNum = Number(updated.pricePerSqFt || 0);
      const discountPercent = Number(updated.companyDiscountPercent || 0);
      const plcPercent = Number(updated.plcPercent || 0);
      
      let area = 0;
      if (updated.rateType === 'Carpet Area') area = Number(updated.carpetArea || 0);
      else if (updated.rateType === 'Plot Area') area = Number(updated.totalSize || 0);
      else area = Number(updated.builtUpArea || 0); // Default to Built-up Area

      if (rateNum && area) {
        const discountPerSqFt = rateNum * (discountPercent / 100);
        const discountAmount = area * discountPerSqFt;
        const discountedRate = rateNum - discountPerSqFt;
        const baseAmount = area * discountedRate;
        const plcAmount = area * rateNum * (plcPercent / 100);
        updated.amount = (baseAmount + plcAmount).toFixed(2);
        updated.discountPerSqFt = discountPerSqFt.toFixed(2);
        updated.discountAmount = discountAmount.toFixed(2);
      }
      
      return updated;
    });
  };

  const handleSaveEdit = async () => {
    setSavingEdit(true);
    try {
      const payload = {
        ...editFormData,
        inventoryType: 'Villa',
        projectType: 'Villa',
        status: editFormData.status.toLowerCase(),
      };
      const response = await fetch(`${API_BASE}/api/projects/inventory/${editFormData.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (response.ok) {
        pushSuccessMessage('Villa updated successfully!');
        setEditDialogOpen(false);
        fetchVillaInventory();
      } else {
        throw new Error('Update failed');
      }
    } catch (error) {
      pushErrorMessage('Failed to update villa.');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDelete = async (villa) => {
    const plotId = villa?._rawData?._id || villa?.id;
    if (!plotId || !window.confirm('Are you sure you want to delete this villa?')) return;

    setDeleteLoadingId(plotId);
    try {
      const response = await fetch(`${API_BASE}/api/projects/inventory/${plotId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isDeleted: true }),
      });
      if (response.ok) {
        pushSuccessMessage('Villa deleted successfully!');
        fetchVillaInventory();
      } else {
        throw new Error('Deletion failed');
      }
    } catch (error) {
      pushErrorMessage('Failed to delete villa.');
    } finally {
      setDeleteLoadingId(null);
    }
  };

  const getStatusStyle = (status) => {
    const s = status?.toLowerCase();
    if (s === 'available') return { backgroundColor: 'rgba(76, 175, 80, 0.12)', color: '#2e7d32' };
    if (s === 'booked') return { backgroundColor: 'rgba(25, 118, 210, 0.12)', color: '#1976d2' };
    if (s?.includes('construction')) return { backgroundColor: 'rgba(255, 193, 7, 0.12)', color: '#f57c00' };
    if (s === 'hold') return { backgroundColor: 'rgba(244, 67, 54, 0.12)', color: '#d32f2f' };
    return { backgroundColor: 'rgba(158, 158, 158, 0.12)', color: '#616161' };
  };

  const handleView = (villa) => {
    setSelectedVilla(villa);
    setViewDialogOpen(true);
  };

  const handleCloseView = () => {
    setViewDialogOpen(false);
    setSelectedVilla(null);
  };

  const handleImportFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      setImportFileName(file.name);
      const rows = await readExcelFile(file);
      pushSuccessMessage(`Imported ${rows.length} records. (Local display only)`);
      // Here you would normally send to server
    } catch (err) {
      pushErrorMessage('Import failed');
    }
  };

  const filteredData = villas;

  if (!canRead) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '70vh', p: 3 }}>
        <Paper elevation={3} sx={{ p: { xs: 3, md: 6 }, textAlign: 'center', maxWidth: 500, borderRadius: 3, boxShadow: '0 10px 40px rgba(0,0,0,0.1)' }}>
          <AvatarMui sx={{ bgcolor: '#fee2e2', color: '#ef4444', width: 80, height: 80, mb: 3, mx: 'auto' }}>
            <WarningIcon sx={{ fontSize: 50 }} />
          </AvatarMui>
          <Typography variant="h4" fontWeight="bold" color="#1a202c" gutterBottom>
            Access Denied
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4, fontSize: '1.1rem' }}>
            You do not have permission to view Villa Inventory. Please contact your administrator for access.
          </Typography>
          <Button
            variant="contained"
            onClick={() => history.push('/app/dashboard')}
            sx={{ 
              backgroundColor: '#536DFE', 
              '&:hover': { backgroundColor: '#3d5afe' },
              px: 4, py: 1.2, borderRadius: 2, fontWeight: 600, textTransform: 'none' 
            }}
          >
            Back to Dashboard
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, mr: 5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <VillaIcon sx={{ mr: 1, color: 'primary.main', fontSize: 28 }} />
            <Typography variant="h4" fontWeight="bold" color="primary">Villa List</Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
          {canWrite && (
            <>
              <Button variant="contained" startIcon={<FileUploadIcon />} onClick={() => setImportDialogOpen(true)}>Import</Button>
              <ExportButton onExportPdf={() => exportAsPDF({ rows: buildExportRows(filteredData), title: 'Villas' })} variant="outlined" />
              <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddNew} disabled={pendingProjectFilter === 'all'}>Add New Villa</Button>
            </>
          )}
          <RefreshButton
            onClick={fetchVillaInventory}
            loading={loadingVillas}
            disabled={pendingProjectFilter === 'all'}
            sx={{ height: { xs: 'auto', sm: 40 } }}
          />
        </Box>
      </Box>

      {successMessage && <Alert severity="success" sx={{ mb: 2 }}>{successMessage}</Alert>}
      {errorMessage && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage}</Alert>}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel>Project</InputLabel>
              <Select
                value={pendingProjectFilter}
                onChange={(e) => setPendingProjectFilter(e.target.value)}
                label="Project"
              >
                <MenuItem value="all">Select Project</MenuItem>
                {projects.map(p => <MenuItem key={p._id} value={p.projectName}>{p.projectName}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs="auto">
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => setPendingProjectFilter('all')}>Reset</Button>
          </Grid>
        </Grid>
      </Paper>

      {loadingVillas ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 10 }}><CircularProgress /></Box>
      ) : filteredData.length > 0 ? (
        <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8f9fa' }}>
                  <TableCell>Villa Name</TableCell>
                  <TableCell>Width/Depth</TableCell>
                  <TableCell>Area</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Facing</TableCell>
                  <TableCell align="right">Discount / Amount</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredData.map((villa) => (
                  <TableRow key={villa.id} hover>
                    <TableCell><Typography fontWeight="600">{villa.name}</Typography></TableCell>
                    <TableCell>{villa.plotWidth} x {villa.plotDepth}</TableCell>
                    <TableCell>{villa.plotArea}</TableCell>
                    <TableCell><Chip label={villa.type} size="small" color="primary" /></TableCell>
                    <TableCell>{villa.facing}</TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                        <Typography variant="body2" color="text.secondary">
                          ₹{Number(villa.discountPerSqFt || 0).toLocaleString('en-IN')}/sq.ft
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          ₹{Number(villa.discountAmount || 0).toLocaleString('en-IN')}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right">{villa.amount}</TableCell>
                    <TableCell><Chip label={villa.status} size="small" sx={getStatusStyle(villa.status)} /></TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                        <IconButton size="small" color="info" onClick={() => handleView(villa)}><VisibilityIcon /></IconButton>
                        {canWrite ? (
                          <>
                            <IconButton size="small" color="primary" onClick={() => handleEdit(villa)}><EditIcon /></IconButton>
                            <IconButton size="small" color="error" onClick={() => handleDelete(villa)}>
                              {deleteLoadingId === villa.id ? <CircularProgress size={18} /> : <DeleteIcon />}
                            </IconButton>
                          </>
                        ) : (
                          <></>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          />
        </Paper>
      ) : (
        <Card sx={{ p: 10, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            {pendingProjectFilter === 'all' ? 'Select a project to view villas' : 'No villas found for this project'}
          </Typography>
        </Card>
      )}

      {/* View Dialog */}
      <Dialog open={viewDialogOpen} onClose={handleCloseView} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Villa Details
          <IconButton onClick={handleCloseView}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedVilla && (
            <Grid container spacing={2}>
              {Object.entries({
                'Villa ID': selectedVilla.villaId,
                'Name': selectedVilla.name,
                'Project': selectedVilla.project,
                'Type': selectedVilla.type,
                'Status': selectedVilla.status,
                'Width': selectedVilla.plotWidth,
                'Depth': selectedVilla.plotDepth,
                'Area': selectedVilla.plotArea,
                'Facing': selectedVilla.facing,
                'Rate': selectedVilla.rate,
                'Discount Per Sq.ft': `₹${Number(selectedVilla.discountPerSqFt || 0).toLocaleString('en-IN')}`,
                'Discount Amount': `₹${Number(selectedVilla.discountAmount || 0).toLocaleString('en-IN')}`,
                'Amount': selectedVilla.amount,
                'Remark': selectedVilla.remark
              }).map(([label, val]) => (
                <Grid item xs={12} sm={6} md={4} key={label}>
                  <TextField label={label} value={val || '—'} fullWidth disabled />
                </Grid>
              ))}
            </Grid>
          )}
        </DialogContent>
        <DialogActions><Button onClick={handleCloseView}>Close</Button></DialogActions>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={importDialogOpen} onClose={() => setImportDialogOpen(false)}>
        <DialogTitle>Import Villas</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>Select excel file to import villa data.</Typography>
          <input type="file" onChange={handleImportFile} accept=".xlsx,.xls" />
          {importFileName && <Typography sx={{ mt: 1 }}>Selected: {importFileName}</Typography>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportDialogOpen(false)}>Cancel</Button>
          <Button variant="contained">Import Now</Button>
        </DialogActions>
      </Dialog>
      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Edit Villa
          <IconButton onClick={() => setEditDialogOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {editFormData && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField label="Villa Name/ID" value={editFormData.plotName} onChange={(e) => handleEditFieldChange('plotName', e.target.value)} fullWidth />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel>Type</InputLabel>
                  <Select value={editFormData.type} onChange={(e) => handleEditFieldChange('type', e.target.value)} label="Type">
                    {villaTypeOptions.map(opt => <MenuItem key={opt.id} value={opt.label}>{opt.label}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select value={editFormData.status} onChange={(e) => handleEditFieldChange('status', e.target.value)} label="Status">
                    {progressOptions.map(opt => <MenuItem key={opt.id} value={opt.id}>{opt.label}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField label="Width" value={editFormData.width} onChange={(e) => handleEditFieldChange('width', e.target.value)} fullWidth type="number" />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField label="Depth" value={editFormData.length} onChange={(e) => handleEditFieldChange('length', e.target.value)} fullWidth type="number" />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField label="Area" value={editFormData.totalSize} fullWidth disabled />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField label="Floors" value={editFormData.floors} onChange={(e) => handleEditFieldChange('floors', e.target.value)} fullWidth type="number" />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField label="Rate" value={editFormData.pricePerSqFt} onChange={(e) => handleEditFieldChange('pricePerSqFt', e.target.value)} fullWidth type="number" />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField label="Discount (%)" value={editFormData.companyDiscountPercent} onChange={(e) => handleEditFieldChange('companyDiscountPercent', e.target.value)} fullWidth type="number" />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField label="Discount Per Sq.ft" value={editFormData.discountPerSqFt} fullWidth disabled />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField label="Discount Amount" value={editFormData.discountAmount} fullWidth disabled />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField label="PLC (%)" value={editFormData.plcPercent} onChange={(e) => handleEditFieldChange('plcPercent', e.target.value)} fullWidth type="number" />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField label="Total Amount" value={editFormData.amount} fullWidth disabled />
              </Grid>
              <Grid item xs={12} sm={8}>
                <TextField label="Remark" value={editFormData.remark} onChange={(e) => handleEditFieldChange('remark', e.target.value)} fullWidth multiline rows={2} />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveEdit} disabled={savingEdit}>
            {savingEdit ? <CircularProgress size={20} /> : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const buildExportRows = (data) => data.map(v => ({
  'Name': v.name,
  'Project': v.project,
  'Type': v.type,
  'Status': v.status,
  'Amount': v.amount
}));

export default Villa;
